import { BaseClientConnector } from './BaseClientConnector.js';
import { ProtocolArray, PROTOCOLS } from '@gotti/core';
import { Client } from "../Client.js";
import type { RemoteInfo, Socket } from 'dgram';
export type ClientConnectorUDPOptions = {
	listenHost?: string, 
	listenPort: number 
}

export class ClientConnectorUDP extends BaseClientConnector {
    private udpClient: Socket;
    private serverHost: string;
    private serverPort: number;
    private listenHost: string;
    private initialConnectInterval?: NodeJS.Timeout;
    private listenPort: number;
    private readonly receivedAcks: number[] = [];
    private readonly pendingAcks: {[ack: number]: {
        message: string,
        seq?: number,
        interval?: any,
    }} = {}
    private ackTimeout?: NodeJS.Timeout;
    private readonly receivedOutOfOrderSeqs: {[seq: number]: ProtocolArray } = {}
    private lastAck: number = 0;
    private lastSeq: number = 0;
    private lastProcessedSeq: number = 0;

    private bound  = false;

    constructor(client: Client) {
        super(client);
    }

    public async connect<ClientConnectorUDPOptions>(opts) {
			let dgram: typeof import('dgram') | undefined;

			if (typeof window === 'undefined') {
				dgram = await import('dgram');
			}
			if(!dgram) {
				throw new Error('UDP protocol is not supported.');
			}

			this.udpClient = dgram.createSocket('udp4');
			// Socket death → drive the auto-reconnect loop (mirrors the websocket
			// wiring). onConnectionLost is cleared FIRST by disconnect() on a
			// deliberate teardown, so these fire only on an unexpected drop.
			// NOTE (scope cut): UDP is connectionless — a dead SERVER produces no
			// socket-level 'close'/'error' here, so this only catches local
			// socket faults; true server-death detection needs heartbeat timeout
			// (future work). WebRTC/UDP peers re-negotiate fresh on rejoin.
			this.udpClient.on('close', () => this.onConnectionLost?.());
			this.udpClient.on('error', () => this.onConnectionLost?.());
			this.serverHost = opts.host;
			this.serverPort = opts.port;
			this.listenHost = opts.listenHost || '127.0.0.1';
			this.listenPort = opts.listenPort;
			this.publicId = opts.publicId;
			this.privateId = opts.privateId;
			let resolved = false;
			return new Promise<boolean>((resolve, reject) => {
				//console.log('listen:', this.listenHost, this.listenPort);
				// Bind the client to the specified local address and port
				this.bound = true;
				this.udpClient.bind(this.listenPort, this.listenHost, () => {
					const ackTimeout = () => {
						if(this.receivedAcks.length !== 0) {
							this.sendMessage(JSON.stringify([
								PROTOCOLS.SERVER_ACK,
								...this.receivedAcks,
								this.privateId,
							]));
						}
						this.ackTimeout = setTimeout(ackTimeout, 500);
					}
					
					this.ackTimeout = setTimeout(ackTimeout, 500);

					// Listen for incoming messages
					this.udpClient.on('message', (msg: Buffer, rinfo: RemoteInfo) => {
						const parsed = JSON.parse(msg.toString());
						if(parsed[0] === PROTOCOLS.PING) {
							if(!resolved) {
									resolved = true;
									resolve(true);
							}
							parsed.push(this.privateId)
							this.sendMessage(JSON.stringify(parsed));
							return;
						}
						
						if (parsed[0] === PROTOCOLS.SERVER_ACK) {
							this.handleAck(parsed);
						} else if (parsed[0] === PROTOCOLS.CLIENT_ACK) {
							this.handleConfirmAck(parsed);
						} else {
							// console.log('PARSED WAS ARRAY', parsed);
							const [ payloadString, ack, seq ] = parsed;
							this.handleIncomingMessage(JSON.parse(payloadString), ack, seq);
						}
					});


					// Send an initial message to the server to identify this client
					// console.log('sending message', [ this.publicId, this.privateId ])
					const initialJoinMessage = JSON.stringify([
							this.publicId,
							this.privateId,
					]);
					let retries = 0;
					this.initialConnectInterval = setInterval(() => {
							if(resolved) { 
									clearInterval(this.initialConnectInterval);
									return;
							}
							retries++;
							if(retries > 5) {
									clearInterval(this.initialConnectInterval);
									reject(new Error('Failed to connect to server, timeout.'));
							}
							this.sendMessage(initialJoinMessage);
					}, 2000);
			});

				// Handle error binding
				this.udpClient.on('error', (err) => {
					console.error(`UDP Client Error: ${err?.toString?.() || err}`);
					reject(err);
				});
			});
    }


    private handleAck(payload: any[]) {
			for(let i = 1; i < payload.length; i++) {
				const a = payload[i];
				if(this.pendingAcks[a]) {
					clearInterval(this.pendingAcks[a].interval);
					delete this.pendingAcks[a];
				}
			}
			payload.push(this.privateId);
			const ackMessage = JSON.stringify(payload);
			this.sendMessage(ackMessage);
    }

    private handleConfirmAck(ackPayload: number[]) {
        for(let i = 1; i < ackPayload.length; i++) {
            const a = ackPayload[i]
            const idx = this.receivedAcks.indexOf(a);
            if(idx > -1) {
                this.receivedAcks.splice(idx, 1);
            }
        }
    }

    private handleIncomingMessage(payload: ProtocolArray, ack?: number, seq?: number) {
        // console.log('handleIncomingMessage', { payload, ack, seq })
        if (ack && this.receivedAcks.includes(ack)) return; // Duplicate ACK check

        if (ack) {
            this.receivedAcks.push(ack);
            const ackMessage = JSON.stringify([PROTOCOLS.CLIENT_ACK, ack, this.privateId]);
            this.sendMessage(ackMessage);
        }

        if (seq) {
            if (seq === this.lastProcessedSeq + 1) {
                // Process in-order message
                this.onServerMessage(payload);
                this.lastProcessedSeq = seq;

                // Process any stored out-of-order messages
                let nextSeq = seq + 1;
                while (this.receivedOutOfOrderSeqs[nextSeq]) {
                    const nextPayload = this.receivedOutOfOrderSeqs[nextSeq];
                    delete this.receivedOutOfOrderSeqs[nextSeq];
                    this.onServerMessage(nextPayload);
                    this.lastProcessedSeq = nextSeq;
                    nextSeq++;
                }
            } else if (seq > this.lastProcessedSeq + 1) {
                // Store out-of-order messages
                this.receivedOutOfOrderSeqs[seq] = payload;
            }
        } else {
          //  console.log('PROCESS SERVER MESSAGE', payload)
            // Process message without sequence
            this.onServerMessage(payload);
        }
    }

    public send(data: ProtocolArray, reliable = false, ordered = false) {
        data.push(this.privateId);

        if (reliable) {
            let seq;
            const ackId = ++this.lastAck;
            data.push(ackId);
            if (ordered) {
                seq = ++this.lastSeq;
                data.push(seq); 
            }
            const message = JSON.stringify(data);
            this.sendMessageWithRetry(message, ackId, seq);
        } else {
            const message = JSON.stringify(data);
            this.sendMessage(message);
        }
    }

    private sendMessageWithRetry(message: string, ackId: number, seq?: number) {
        this.sendMessage(message);
        
        const interval = setInterval(() => {
            if (this.pendingAcks[ackId]) {
                this.sendMessage(message);
            } else {
                clearInterval(interval);
            }
        }, 250);

        this.pendingAcks[ackId] = { message, seq, interval };
    }

    private sendMessage(message: string | Buffer) {
        this.udpClient.send(message, 0, message.length, this.serverPort, this.serverHost, (err) => {
            if (err) {
                console.error('Error sending message:', err);
            }
        });
    }

    public async onDisconnect() {
        //todo: send disconnect message
       // console.log('sending close message!!!!!!')

      
        clearInterval(this.initialConnectInterval);
        if (this.ackTimeout) {
            clearTimeout(this.ackTimeout);
        }

        const acks = Object.keys(this.pendingAcks);

        acks.forEach(ack => {
            clearInterval(this.pendingAcks[ack].interval);
            delete this.pendingAcks[ack];
        })
        this.receivedAcks.length = 0;
        const outOfOrderSeqs = Object.keys(this.receivedOutOfOrderSeqs);
        outOfOrderSeqs.forEach(seq => {
            delete this.receivedOutOfOrderSeqs[seq];
        })

        return new Promise((resolve) => {
            if(this.udpClient) {
              //  console.log('serverPort:', this.serverPort, 'serverHost:', this.serverHost);
                if(!this.bound) {
                    this.udpClient.close();
                    this.udpClient = undefined;
                    return resolve(true);
                }
                const msg = JSON.stringify([PROTOCOLS.CLIENT_CLOSE, this.privateId]);
                this.udpClient.send(msg, 0, msg.length, this.serverPort, this.serverHost, (err) => {
                    if (err) {
                        console.error('Error sending CLIENT_CLOSE message:', err);
                    }
                    this.udpClient.close();
                    this.udpClient = undefined;
                    this.bound = false;
                    resolve(true);
                });
            } else {
                resolve(true);
            }    
        })
    }
}
